import React, { useState, useEffect } from 'react';
import './CheckInManagement.css';
import apiService from '../services/api.service';

// Time conversion utilities
const minutesToTimeString = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
};

const formatDateTime = (dateTimeString) => {
  const date = new Date(dateTimeString);
  return date.toLocaleString('tr-TR', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

const CheckInManagement = () => {
  const [currentMonth, setCurrentMonth] = useState(
    new Date().toISOString().slice(0, 7) // YYYY-MM format
  );
  const [checkInData, setCheckInData] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [groupBy, setGroupBy] = useState('employee'); // 'employee' or 'date'

  // Fetch employees
  const fetchEmployees = async () => {
    try {
      const response = await apiService.getEmployees({ isActive: true });
      setEmployees(response.data);
    } catch (err) {
      console.error('Error fetching employees:', err);
      setError('Çalışanlar yüklenirken bir hata oluştu: ' + err.message);
    }
  };

  // Fetch check-in data for the selected month
  const fetchCheckInData = async () => {
    setLoading(true);
    try {
      const response = await apiService.getMonthlyCheckInData(currentMonth, selectedEmployee);
      // API response.data should contain the actual array
      const data = response.data?.data || response.data || [];
      setCheckInData(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err) {
      console.error('Error fetching check-in data:', err);
      setError('Giriş çıkış verileri yüklenirken bir hata oluştu: ' + err.message);
      setCheckInData([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  // Load data when component mounts or filters change
  useEffect(() => {
    fetchEmployees();
  }, []);

  useEffect(() => {
    fetchCheckInData();
  }, [currentMonth, selectedEmployee]);

  // Debug log to check data structure
  useEffect(() => {
    console.log('CheckInData:', checkInData);
    console.log('Is array:', Array.isArray(checkInData));
  }, [checkInData]);

  // Get previous month
  const getPreviousMonth = () => {
    const date = new Date(currentMonth + '-01');
    date.setMonth(date.getMonth() - 1);
    setCurrentMonth(date.toISOString().slice(0, 7));
  };

  // Get next month
  const getNextMonth = () => {
    const date = new Date(currentMonth + '-01');
    date.setMonth(date.getMonth() + 1);
    setCurrentMonth(date.toISOString().slice(0, 7));
  };

  // Go to current month
  const goToCurrentMonth = () => {
    setCurrentMonth(new Date().toISOString().slice(0, 7));
  };

  // Get employee name
  const getEmployeeName = (employeeId) => {
    const employee = employees.find(emp => emp.id === employeeId);
    return employee ? `${employee.name} ${employee.surname}` : 'Bilinmeyen Çalışan';
  };

  // Calculate delay status
  const calculateDelayStatus = (shiftStartTime, checkInTime) => {
    if (!shiftStartTime || !checkInTime) return null;
    
    const shiftStart = new Date(`1970-01-01T${minutesToTimeString(shiftStartTime)}:00`);
    const checkIn = new Date(checkInTime);
    
    // Extract only time part for comparison
    const shiftStartMinutes = shiftStart.getHours() * 60 + shiftStart.getMinutes();
    const checkInMinutes = checkIn.getHours() * 60 + checkIn.getMinutes();
    
    const delayMinutes = checkInMinutes - shiftStartMinutes;
    
    if (delayMinutes > 15) {
      return { status: 'late', delay: delayMinutes };
    } else if (delayMinutes > 0) {
      return { status: 'slightly-late', delay: delayMinutes };
    } else {
      return { status: 'on-time', delay: delayMinutes };
    }
  };

  // Group data by employee or date
  const groupedData = Array.isArray(checkInData) ? checkInData.reduce((acc, item) => {
    const groupKey = groupBy === 'employee' ? item.employeeId : item.date;
    if (!acc[groupKey]) {
      acc[groupKey] = [];
    }
    acc[groupKey].push(item);
    return acc;
  }, {}) : {};

  // Get group display name
  const getGroupDisplayName = (groupKey) => {
    if (groupBy === 'employee') {
      return getEmployeeName(parseInt(groupKey));
    } else {
      return formatDate(groupKey);
    }
  };

  // Get group subtitle
  const getGroupSubtitle = (records) => {
    if (groupBy === 'employee') {
      return `${records.length} gün`;
    } else {
      const uniqueEmployees = [...new Set(records.map(r => r.employeeId))].length;
      return `${uniqueEmployees} çalışan`;
    }
  };

  return (
    <div className="checkin-management">
      <div className="section-header">
        <h2>Giriş Çıkış Takibi</h2>
        
        <div className="controls">
          <div className="month-navigation">
            <button 
              onClick={getPreviousMonth} 
              className="nav-button"
              title="Önceki Ay"
            >
              <span className="icon">«</span>
              <span className="nav-text">Önceki Ay</span>
            </button>
            
            <button 
              onClick={goToCurrentMonth} 
              className="current-month-button"
            >
              Bu Ay
            </button>
            
            <button 
              onClick={getNextMonth} 
              className="nav-button"
              title="Sonraki Ay"
            >
              <span className="nav-text">Sonraki Ay</span>
              <span className="icon">»</span>
            </button>
          </div>
          
          <div className="month-display">
            <h3>
              {new Date(currentMonth + '-01').toLocaleDateString('tr-TR', { 
                month: 'long', 
                year: 'numeric' 
              })}
            </h3>
          </div>
          
          <div className="filter-controls">
            <div className="group-toggle">
              <button 
                className={groupBy === 'employee' ? 'active' : ''}
                onClick={() => setGroupBy('employee')}
                title="Çalışanlara Göre Grupla"
              >
                <span className="icon">👤</span>
                Çalışanlara Göre
              </button>
              <button 
                className={groupBy === 'date' ? 'active' : ''}
                onClick={() => setGroupBy('date')}
                title="Günlere Göre Grupla"
              >
                <span className="icon">📅</span>
                Günlere Göre
              </button>
            </div>
            
            <select 
              value={selectedEmployee} 
              onChange={(e) => setSelectedEmployee(e.target.value)}
              className="employee-filter"
            >
              <option value="">Tüm Çalışanlar</option>
              {employees.map(employee => (
                <option key={employee.id} value={employee.id}>
                  {employee.name} {employee.surname}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="loading">Yükleniyor...</div>
      ) : error ? (
        <div className="error-message">{error}</div>
      ) : (
        <div className="checkin-data">
          {Object.keys(groupedData).length === 0 ? (
            <div className="no-data">Bu ay için giriş çıkış verisi bulunamadı.</div>
          ) : (
            Object.entries(groupedData).map(([groupKey, records]) => (
              <div key={groupKey} className="employee-section">
                <div className="employee-header">
                  <h3>{getGroupDisplayName(groupKey)}</h3>
                  <span className="record-count">{getGroupSubtitle(records)}</span>
                </div>
                
                <div className="records-table">
                  <table>
                    <thead>
                      <tr>
                        {groupBy === 'date' && <th>Çalışan</th>}
                        {groupBy === 'employee' && <th>Tarih</th>}
                        <th>Vardiya Türü</th>
                        <th>Planlanan Saat</th>
                        <th>Giriş Saati</th>
                        <th>Durum</th>
                        <th>Konum Durumu</th>
                      </tr>
                    </thead>
                    <tbody>
                      {records
                        .sort((a, b) => {
                          if (groupBy === 'employee') {
                            return new Date(a.date) - new Date(b.date);
                          } else {
                            return a.employeeName.localeCompare(b.employeeName, 'tr-TR');
                          }
                        })
                        .map((record, index) => {
                          const delayStatus = calculateDelayStatus(
                            record.shiftStartTime, 
                            record.checkInTime
                          );
                          
                          return (
                            <tr key={index}>
                              {groupBy === 'date' && (
                                <td>
                                  <span className="employee-name">
                                    {record.employeeName}
                                  </span>
                                </td>
                              )}
                              {groupBy === 'employee' && (
                                <td>{formatDate(record.date)}</td>
                              )}
                              <td>
                                <span 
                                  className="shift-type-badge"
                                  style={{ backgroundColor: record.shiftTypeColor }}
                                >
                                  {record.shiftTypeName}
                                </span>
                              </td>
                              <td>
                                {record.shiftStartTime ? 
                                  minutesToTimeString(record.shiftStartTime) : 
                                  'Vardiya yok'
                                }
                              </td>
                              <td>
                                {record.checkInTime ? 
                                  formatDateTime(record.checkInTime) : 
                                  <span className="no-checkin">Giriş yapılmadı</span>
                                }
                              </td>
                              <td>
                                {delayStatus && (
                                  <span className={`status-badge ${delayStatus.status}`}>
                                    {delayStatus.status === 'on-time' && 'Zamanında'}
                                    {delayStatus.status === 'slightly-late' && `${delayStatus.delay} dk gecikme`}
                                    {delayStatus.status === 'late' && `${delayStatus.delay} dk gecikme`}
                                  </span>
                                )}
                                {!record.checkInTime && (
                                  <span className="status-badge absent">Gelmedi</span>
                                )}
                              </td>
                              <td>
                                {record.verificationStatus && (
                                  <span className={`verification-badge ${record.verificationStatus}`}>
                                    {record.verificationStatus === 'verified' ? 'Doğrulandı' : 'Reddedildi'}
                                  </span>
                                )}
                                {record.distance && (
                                  <span className="distance-info">
                                    ({Math.round(record.distance)}m)
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default CheckInManagement;